import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { BehaviorSubject, catchError, distinctUntilChanged, map, Observable, tap, throwError } from "rxjs";
import { Router } from "@angular/router";
import { CachedAPIRequest } from "../../classes";
import { APIRequestResources } from "../../enums";
import { ApiConfig } from "../../config";
import { AuthResponse } from "../../types/userAuth.types";
import { environment } from "../../../../environments/environment";

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService extends CachedAPIRequest {
  /** Auth-key exchange endpoint: ${API_BASE}/auth/login/web */
  private readonly API_URL = `${ApiConfig.authURL}/web`;
  private readonly PORTAL_URL = `${environment.PORTAL}`;
  public readonly APP_NAMESPACE = 'boardroom'
  private readonly authState$ = new BehaviorSubject<boolean>(this.hasValidSession());

  readonly isAuthenticated$ = this.authState$.asObservable().pipe(
    distinctUntilChanged()
  );

  constructor(protected override http: HttpClient, protected router: Router) {
    super(http, APIRequestResources.AuthenticationService);
  }

  /**
   * Exchanges the portal-issued auth-key for a session. The backend returns
   * the user info (name / userName / tokens), which we persist so the rest of
   * the app (e.g. the dashboard greeting) can read it.
   */
  login(authKey: string): Observable<boolean> {
    const headers = new HttpHeaders().set('auth-key', authKey);

    return this.http.post<{ data: AuthResponse }>(this.API_URL, {}, { headers }).pipe(
      map(response => response.data),
      tap(authData => this.storeSession(authData)),
      map(() => true),
      catchError(error => {
        console.error('Authentication error:', error);
        this.clearSession();
        return throwError(() => new Error('Authentication failed'));
      })
    );
  }

  logout(): void {
    this.clearSession();
    window.location.href = this.PORTAL_URL;
  }

  private storeSession(authData: AuthResponse): void {
    if (!authData?.accessToken || !authData?.refreshToken) {
      throw new Error('Invalid authentication response');
    }

    // The /web response only carries `username` ("tharuka"); the nicely-cased
    // display name ("Tharuka") lives inside the access-token JWT.
    const name = this.nameFromToken(authData.accessToken) ?? authData.username;

    sessionStorage.setItem(`${this.APP_NAMESPACE}_access_token`, authData.accessToken);
    sessionStorage.setItem(`${this.APP_NAMESPACE}_user_info`, JSON.stringify({
      name,
      userName: authData.username,
      permissions: authData.userPermissions ?? []
    }));

    this.authState$.next(true);
  }

  /** Best-effort decode of the `name` claim from a JWT payload. */
  private nameFromToken(token: string): string | undefined {
    try {
      const payload = token.split('.')[1];
      if (!payload) return undefined;
      const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(json)?.name || undefined;
    } catch {
      return undefined;
    }
  }

  getsession(): string | null {
    console.log("klpjolkpjio");

    console.log(`${this.APP_NAMESPACE}_access_token`);

    const accessToken = sessionStorage.getItem(`${this.APP_NAMESPACE}_access_token`);
    console.log('accessToken',accessToken);

    return accessToken
  }

  private getUserInfo(): { name?: string; userName?: string; permissions?: Array<{ resourcePath?: string; ability?: string }> } | undefined {
    const userInfo = sessionStorage.getItem(`${this.APP_NAMESPACE}_user_info`);
    if (!userInfo) {
      return undefined;
    }
    try {
      return JSON.parse(userInfo);
    } catch (error) {
      console.error('Error parsing user info:', error);
      return undefined;
    }
  }

  /** Login handle, e.g. "tharuka". */
  public getUsername(): string | undefined {
    return this.getUserInfo()?.userName || undefined;
  }

  /** Human display name, e.g. "Tharuka". */
  public getName(): string | undefined {
    return this.getUserInfo()?.name || undefined;
  }

  /** True when the user holds a granted permission for the given resource path. */
  public hasPermission(resourcePath: string): boolean {
    return (this.getUserInfo()?.permissions ?? []).some(
      (p) => p.resourcePath === resourcePath && p.ability === 'can'
    );
  }

  private clearSession(): void {
    sessionStorage.removeItem(`${this.APP_NAMESPACE}_access_token`);
    sessionStorage.removeItem(`${this.APP_NAMESPACE}_user_info`);
    this.authState$.next(false);
  }

  private hasValidSession(): boolean {
    return !!sessionStorage.getItem(`${this.APP_NAMESPACE}_access_token`);
  }

  public homePage(): void {
    this.router.navigate(['/dashboard']);
  }


}
